/**
 * @file index.tsx
 *
 * @fileoverview Renders a single date picker.
 */
import { FormItem } from "./../../../01-forms/11-form-item";
import { FormLabel } from "./../../../01-forms/11-form-item/01-form-label";
import { FormDescription } from "./../../../01-forms/11-form-item/02-form-description";
import format from "date-fns/format";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import styles from "../date.module.scss";
import { dateLocalesMap } from "./../../../01-forms/06-date/utils";
import { cva } from "class-variance-authority";
import { useIsMobile } from "./../../../../../hooks/useIsMobile";
import "react-datepicker/dist/react-datepicker.css";
import useMaxDate from "../hooks/useMaxDate";

const wrapper = cva([styles.wrapper], {
  variants: {
    error: {
      true: styles.wrapperError,
    },
  },
});

export interface SingleDatePopupProps {
  /**
   * The id of the date picker.
   */
  id: string;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * If the field is required.
   *
   * @default false
   */
  required?: boolean;
  /**
   * The label to display on the date picker.
   */
  label?: string;
  /**
   * Description for this field.
   */
  description?: string;
  /**
   * The placeholder to display on the date picker input.
   */
  placeholder?: string;
  /**
   * The initial date to set on the date picker.
   */
  initialDate?: any;
  /**
   * The date format to show the date in.
   */
  displayFormat?: string;
  /**
   * Min date value.
   */
  minDate?: any;
  /**
   * Max date value.
   */
  maxDate?: any;
  /**
   * Even to trigger when the value changes.
   */
  onDateChange: (date: any) => void;
  /**
   * If time selection should be visible.
   */
  showTimeSelect?: boolean;
  /**
   * The time format.
   */
  timeFormat?: string;
  /**
   * The intervals for each value in time select.
   */
  timeIntervals?: number;
  /**
   * The title for time selection.
   */
  timeCaption?: string;
  /**
   * If should be read only (prevent keyboard open).
   *
   * @default "false"
   */
  readOnly?: boolean;
  /**
   * Disabled keyboard navigation.
   */
  disabledKeyboardNavigation?: boolean;
  /**
   * To show a month and year dropdown.
   *
   * @default false
   */
  showMonthYearPicker?: boolean;
  /**
   * The format for the calendar
   */
  dateFormatCalendar?: string;
  /**
   * The supported calendar locale.
   */
  locale?: string;
}

/**
 * A date popup can be used inside forms or filters in order to make it easier to choose a date. The single date picker is used for single value
 * dates like a birthdate or a reminder date. Use the "min and max values" prop
 * to allow any date value to be inserted (including past dates).
 */
export const SingleDatePopup = ({
  id = "date",
  placeholder = "Date",
  initialDate,
  onDateChange,
  label,
  description,
  displayFormat = "dd/MM/yyyy",
  showTimeSelect = false,
  timeFormat = "HH:mm",
  timeIntervals = 15,
  timeCaption = "Time",
  minDate,
  maxDate: maxDateProp,
  readOnly = false,
  required = false,
  error = false,
  showMonthYearPicker = false,
  disabledKeyboardNavigation,
  locale,
  ...props
}: SingleDatePopupProps) => {
  const [date, setDate] = useState(initialDate);
  const [months, setMonths] = useState(1);
  const isMobile = useIsMobile();
  const maxDate = useMaxDate(maxDateProp);

  // Update number of months shown when window is small.
  useEffect(() => {
    if (isMobile) {
      setMonths(1);
    }
  }, [isMobile]);

  const datePicker = (
    <div className={wrapper({ error })}>
      {isMobile ? (
        <input
          autoComplete="off"
          value={
            date
              ? showMonthYearPicker
                ? format(date, "yyyy-MM")
                : showTimeSelect
                ? format(date, "yyyy-MM-dd HH:mm")
                : format(date, "yyyy-MM-dd")
              : date
          }
          type={
            showMonthYearPicker
              ? "month"
              : showTimeSelect
              ? "datetime-local"
              : "date"
          }
          readOnly={readOnly}
          {...(minDate ? { min: minDate } : null)}
          max={maxDate.toISOString()}
          onChange={(event) => {
            const dateChanged = !!event.target.value
              ? new Date(event.target.value)
              : null;
            onDateChange(dateChanged);
            setDate(dateChanged);
          }}
        />
      ) : (
        <div>
          <DatePicker
            {...(locale ? { locale: dateLocalesMap[locale] } : null)}
            autoComplete="off"
            id={id}
            selected={date}
            placeholderText={placeholder}
            monthsShown={months}
            dateFormat={displayFormat}
            readOnly={readOnly}
            onChange={(changedDate) => {
              onDateChange(changedDate);
              setDate(changedDate);
            }}
            showMonthYearPicker={showMonthYearPicker}
            {...(minDate ? { minDate } : null)}
            maxDate={maxDate}
            {...(showTimeSelect
              ? {
                  showTimeSelect,
                  timeFormat,
                  timeCaption,
                  timeIntervals,
                }
              : null)}
            disabledKeyboardNavigation={disabledKeyboardNavigation}
            {...props}
          />
        </div>
      )}
    </div>
  );
  if (!label) {
    return datePicker;
  }
  return (
    <FormItem>
      <FormLabel error={error} required={required} htmlFor={id}>
        {label}
      </FormLabel>
      {datePicker}
      {description && <FormDescription>{description}</FormDescription>}
    </FormItem>
  );
};
export default SingleDatePopup;
